import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineInformationCircle, HiOutlineX } from "react-icons/hi";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

import invoiceAmountCheck from "../../assets/svg/invoice-amount-check.svg";
import invoiceGenerate from "../../assets/svg/invoice-generate.svg";
import DateTimePicker from "../../components/DateTimePicker";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import {
  EmailPreviewBlock,
  WhatsAppMessageBlock,
  type InvoiceEmailPreview,
} from "components/InvoiceMessagePreview";
import { Checkbox } from "components/ui/checkbox";
import {
  generateOppositionTmMInvoicePdf,
  getOppositionDiscussionDetails,
  getOppositionTmMInvoiceEligible,
  getOppositionProgress,
  listOppositionTmMInvoices,
  previewOppositionTmMInvoice,
  previewSendOppositionTmMInvoice,
  saveOppositionTmMInvoice,
  sendOppositionTmMInvoicePaymentMessage,
  sendOppositionTmMInvoiceToClient,
  OppositionWorkflowStep,
  type OppositionTmMInvoicePreview,
} from "../../api/opposition";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const OPPOSITION_INVOICE_FOR_TMM_BEFORE_HEARING_FORM_ID =
  "opposition-invoice-for-tmm-before-hearing-form";

const FORM_STYLES = { fontFamily: "Inter, sans-serif" } as const;

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const READONLY_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border";
const INPUT_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";

function formatRupees(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return "Rs. 0/-";
  return `Rs. ${Math.round(v).toLocaleString("en-IN")}/-`;
}

function parseISODate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

function toISODateString(date: Date | null): string | undefined {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDateDisplay(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}-${m}-${y}`;
}

function resolveAddresseeLines(
  addressee: OppositionTmMInvoicePreview["addressee"] | undefined
): string[] {
  if (!addressee) return [];
  const head =
    addressee.designation === "NAME"
      ? (addressee.customName ?? "")
      : (addressee.designation ?? "");
  return [head, addressee.firmName, addressee.place]
    .map((v) => (v ?? "").trim())
    .filter(Boolean);
}

function PreviewRow({
  label,
  children,
  alignTop = false,
}: {
  label: string;
  children: React.ReactNode;
  alignTop?: boolean;
}) {
  const align = alignTop ? "items-start" : "items-baseline";
  return (
    <>
      <div className={`flex ${align} font-medium text-[#222]`}>{label}</div>
      <div className={`flex ${align} text-[#222]`}>:</div>
      <div className="text-[#222] break-words min-w-0">{children}</div>
    </>
  );
}

function InvoiceDetailsPreview({
  preview,
  invoiceDate,
}: {
  preview: OppositionTmMInvoicePreview;
  invoiceDate: Date | null;
}) {
  const addresseeLines = resolveAddresseeLines(preview.addressee);
  const liveInvoiceDateIso = invoiceDate
    ? `${invoiceDate.getFullYear()}-${String(invoiceDate.getMonth() + 1).padStart(2, "0")}-${String(invoiceDate.getDate()).padStart(2, "0")}`
    : null;

  return (
    <div
      className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
      style={FORM_STYLES}
    >
      <PreviewRow label="Addressee" alignTop>
        {addresseeLines.length > 0 ? (
          <div className="flex flex-col">
            {addresseeLines.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </div>
        ) : (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </PreviewRow>
      <PreviewRow label="Form of Address">
        {preview.formOfAddress?.trim() || <span className="text-[#6D6D6D]">—</span>}
      </PreviewRow>
      <PreviewRow label="Mark">
        {preview.markName?.trim() || <span className="text-[#6D6D6D]">—</span>}
      </PreviewRow>
      <PreviewRow label="Selected Class">
        {preview.selectedClass != null ? String(preview.selectedClass) : <span className="text-[#6D6D6D]">—</span>}
      </PreviewRow>
      <PreviewRow label="No. of Applications for TM-M">
        {String(preview.applications.length)}
      </PreviewRow>
      <PreviewRow label="Total Prof Fee">
        {formatRupees(preview.profFeeTotal)}
      </PreviewRow>
      {preview.totalDiscount > 0 && (
        <PreviewRow label="− Total Discount">
          {formatRupees(preview.totalDiscount)}
        </PreviewRow>
      )}
      <PreviewRow label="Invoice Amount">
        {formatRupees(preview.invoiceAmount)}
      </PreviewRow>
      <PreviewRow label="Invoice No. for TM-M">
        {preview.invoiceNo?.trim() || <span className="text-[#6D6D6D]">Auto-generated on save</span>}
      </PreviewRow>
      <PreviewRow label="Date of Invoice for TM-M">
        {liveInvoiceDateIso ? (
          formatDateDisplay(liveInvoiceDateIso)
        ) : (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </PreviewRow>
    </div>
  );
}

function InvoiceApplicationsTable({
  applications,
}: {
  applications: OppositionTmMInvoicePreview["applications"];
}) {
  return (
    <div className="overflow-x-auto rounded-[6px] border border-[#EEEEEE]">
      <table className="w-full border-collapse text-[12px] leading-5">
        <thead className="bg-[#F7F7F7]">
          <tr>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Conflicting Mark
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">
              Application No.
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Class
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Proprietor
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Place
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">
              Date of Hearing
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">
              Prof Fee / App
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Line Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {applications.length === 0 ? (
            <tr>
              <td colSpan={8} className="px-2.5 py-3 text-center text-[#6D6D6D]">
                No applications.
              </td>
            </tr>
          ) : (
            applications.map((app, idx) => (
              <tr
                key={`${app.conflictingMarkId}-${idx}`}
                className={idx < applications.length - 1 ? "border-b border-[#EEEEEE]" : ""}
              >
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.conflictingMark?.trim() || "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">
                  {app.applicationNo?.trim() || "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.classOfMark != null ? String(app.classOfMark) : "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.nameOfProprietor?.trim() || "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.placeOfProprietor?.trim() || "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">
                  {formatDateDisplay(app.dateOfOppositionHearing)}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">
                  {formatRupees(app.profFeePerApp)}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">
                  {formatRupees(app.lineAmount)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceAmountTooltip({
  preview,
  discountInput,
}: {
  preview: OppositionTmMInvoicePreview;
  discountInput: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const n = preview.applications.length;
  const firstApp = preview.applications[0] ?? null;
  const profFee = Number(firstApp?.profFeePerApp ?? 0);

  const parsedDiscount = Number(discountInput);
  const liveDiscount =
    discountInput.trim() === "" || !Number.isFinite(parsedDiscount)
      ? 0
      : Math.max(0, parsedDiscount);

  const profFeeTotal = profFee * n;
  const totalDiscount = liveDiscount * n;
  const invoiceAmount = Math.max(0, profFeeTotal - totalDiscount);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is the invoice amount calculated?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-[#6D6D6D] hover:text-[#222] focus:outline-none focus:ring-1 focus:ring-[#222] rounded-full"
      >
        <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[300px] rounded-[8px] border border-[#EEEEEE] bg-white p-3 shadow-lg"
          style={FORM_STYLES}
        >
          <p className="text-[11px] font-semibold leading-4 text-[#222]">
            How the invoice amount is calculated
          </p>
          <p className="mt-1 text-[11px] leading-4 text-[#6D6D6D]">
            (Prof. Charges − Discount) × No. of applications
          </p>
          <div className="mt-2 flex flex-col gap-1 text-[11px] leading-4 text-[#222]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">
                Prof. Charges ({formatRupees(profFee)}/app × {n})
              </span>
              <span className="font-medium">{formatRupees(profFeeTotal)}</span>
            </div>
            {liveDiscount > 0 && (
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[#6D6D6D]">
                  − Discount ({formatRupees(liveDiscount)}/app × {n})
                </span>
                <span className="font-medium">{formatRupees(totalDiscount)}</span>
              </div>
            )}
            <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-[#EEEEEE] pt-1.5">
              <span className="font-semibold text-[#222]">Invoice amount</span>
              <span className="font-semibold text-[#222]">
                {formatRupees(invoiceAmount)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function OppositionInvoiceForTmMBeforeHearingView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  const [preview, setPreview] = useState<OppositionTmMInvoicePreview | null>(null);
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);
  const [discountInput, setDiscountInput] = useState<string>("");
  const [invoiceAmountInput, setInvoiceAmountInput] = useState<string>("");
  const [includeInvoiceNo, setIncludeInvoiceNo] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<{
    invoiceDate?: boolean;
    discount?: boolean;
    invoiceAmount?: boolean;
  }>({});

  const [pdf, setPdf] = useState<{
    s3Key: string;
    downloadUrl: string;
    filename: string;
  } | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<"details" | "pdf" | "message" | "email">("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");
  const [paymentMessage, setPaymentMessage] = useState<string>("");
  const [emailPreview, setEmailPreview] = useState<InvoiceEmailPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPdfLoading, setPreviewPdfLoading] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadRef = useRef(true);
  const lastSavedRef = useRef<{
    invoiceDate: string | undefined;
    discount: number | undefined;
    invoiceAmount: number | undefined;
    includeInvoiceNo: boolean;
  } | null>(null);

  useEffect(() => {
    onSentStatusChange?.(hasSent);
  }, [hasSent, onSentStatusChange]);

  const loadPreview = async (
    targetLeadId: string
  ): Promise<OppositionTmMInvoicePreview | null> => {
    // Prefer the active invoice on this lead. If none, draft-preview from all eligible apps.
    const invoices = await listOppositionTmMInvoices(targetLeadId).catch(
      () => [] as OppositionTmMInvoicePreview[]
    );
    // Skip an ACTIVE invoice whose line-items were orphaned (e.g. the conflicting
    // marks were re-created by a later Status Details save, cascade-deleting the
    // invoice apps). Such an invoice has zero applications and must not block the
    // step — fall back to drafting a fresh invoice from the current eligible apps.
    const active = invoices.find(
      (inv) => inv.status === "ACTIVE" && inv.applications.length > 0
    );
    if (active?.invoiceId) {
      return previewOppositionTmMInvoice(targetLeadId, {
        invoiceId: active.invoiceId,
      });
    }
    const eligible = await getOppositionTmMInvoiceEligible(targetLeadId);
    const availableIds = eligible.applications
      .filter((a) => a.activeInvoice == null)
      .map((a) => a.conflictingMarkId);
    if (availableIds.length === 0) return null;
    return previewOppositionTmMInvoice(targetLeadId, {
      conflictingMarkIds: availableIds,
    });
  };

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    initialLoadRef.current = true;

    Promise.all([
      loadPreview(leadId),
      getOppositionDiscussionDetails(leadId).catch(() => null),
    ])
      .then(([previewData, details]) => {
        if (cancelled) return;
        setPreview(previewData);
        if (previewData) {
          const firstLine = previewData.applications[0];
          setInvoiceDate(parseISODate(firstLine?.invoiceDate ?? null));
          setDiscountInput(
            firstLine?.discountPerApp ? String(firstLine.discountPerApp) : ""
          );
          const initialAmount =
            previewData.invoiceAmount != null
              ? Math.max(0, Math.round(Number(previewData.invoiceAmount)))
              : 0;
          setInvoiceAmountInput(initialAmount > 0 ? String(initialAmount) : "");
          setIncludeInvoiceNo(previewData.includeInvoiceNo);
          lastSavedRef.current = {
            invoiceDate: firstLine?.invoiceDate ?? undefined,
            discount:
              firstLine?.discountPerApp != null
                ? Math.max(0, Number(firstLine.discountPerApp))
                : undefined,
            invoiceAmount: initialAmount > 0 ? initialAmount : undefined,
            includeInvoiceNo: previewData.includeInvoiceNo,
          };
          if (
            previewData.invoiceDocumentUrl &&
            previewData.invoiceDocumentFilename
          ) {
            setPdf({
              s3Key: "",
              downloadUrl: previewData.invoiceDocumentUrl,
              filename: previewData.invoiceDocumentFilename,
            });
          }
        }
        if (details?.preliminary?.phone) {
          setPhoneNumber(details.preliminary.phone);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load Evidence invoice"
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setTimeout(() => {
            initialLoadRef.current = false;
          }, 0);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  useEffect(() => {
    if (!leadId) {
      setHasSent(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getOppositionProgress(leadId);
        const step = progress.steps.find(
          (s) =>
            s.slug ===
            OppositionWorkflowStep.OP_S3_32b_TmMInvoiceSentToClient
        );
        if (!cancelled && step) setHasSent(step.isCompleted);
      } catch {
        if (!cancelled) setHasSent(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Auto-save (debounced) — shared discount/invoice-date applied to all lines.
  useEffect(() => {
    if (!leadId || !preview) return;
    if (initialLoadRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const parsedDiscount = Number(discountInput);
    const discountForPayload =
      discountInput.trim() === "" || !Number.isFinite(parsedDiscount)
        ? undefined
        : Math.max(0, parsedDiscount);

    const parsedAmount = Number(invoiceAmountInput);
    const amountForPayload =
      invoiceAmountInput.trim() === "" ||
      !Number.isFinite(parsedAmount) ||
      parsedAmount < 0
        ? undefined
        : Math.max(0, Math.round(parsedAmount));

    debounceRef.current = setTimeout(() => {
      const isoDate = toISODateString(invoiceDate);
      const saved = lastSavedRef.current;
      if (
        saved &&
        saved.invoiceDate === isoDate &&
        saved.discount === discountForPayload &&
        saved.invoiceAmount === amountForPayload &&
        saved.includeInvoiceNo === includeInvoiceNo
      ) {
        return;
      }
      const n = preview.applications.length;
      const baseLineAmount =
        amountForPayload !== undefined && n > 0
          ? Math.floor(amountForPayload / n)
          : undefined;
      const remainder =
        amountForPayload !== undefined && n > 0
          ? amountForPayload - baseLineAmount! * n
          : 0;
      saveOppositionTmMInvoice(leadId, {
        invoiceId: preview.invoiceId ?? undefined,
        lines: preview.applications.map((a, idx) => ({
          conflictingMarkId: a.conflictingMarkId,
          ...(discountForPayload !== undefined
            ? { discountPerApp: discountForPayload }
            : {}),
          ...(isoDate ? { invoiceDate: isoDate } : {}),
          ...(baseLineAmount !== undefined
            ? {
                lineAmount:
                  idx === 0 ? baseLineAmount + remainder : baseLineAmount,
              }
            : {}),
        })),
        includeInvoiceNo,
      })
        .then((fresh) => {
          lastSavedRef.current = {
            invoiceDate: isoDate,
            discount: discountForPayload,
            invoiceAmount: amountForPayload,
            includeInvoiceNo,
          };
          setPreview(fresh);
          setPdf(null);
        })
        .catch((err) => {
          toast.error(
            err instanceof Error ? err.message : "Failed to auto-save invoice"
          );
        });
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    invoiceDate?.getTime(),
    discountInput,
    invoiceAmountInput,
    includeInvoiceNo,
    leadId,
  ]);

  const validate = (): boolean => {
    if (!preview) return false;
    const errs: typeof fieldErrors = {};
    if (!invoiceDate) errs.invoiceDate = true;
    const parsedDiscount = Number(discountInput);
    if (
      discountInput.trim() !== "" &&
      (!Number.isFinite(parsedDiscount) || parsedDiscount < 0)
    ) {
      errs.discount = true;
    }
    const parsedAmount = Number(invoiceAmountInput);
    if (
      invoiceAmountInput.trim() !== "" &&
      (!Number.isFinite(parsedAmount) || parsedAmount < 0)
    ) {
      errs.invoiceAmount = true;
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return false;
    }
    setFieldErrors({});
    return true;
  };

  const persistInvoice = async (): Promise<OppositionTmMInvoicePreview | null> => {
    if (!leadId || !preview) return null;
    if (!validate()) return null;
    const parsedDiscount = Number(discountInput);
    const discountForPayload =
      discountInput.trim() === "" || !Number.isFinite(parsedDiscount)
        ? undefined
        : Math.max(0, parsedDiscount);
    const parsedAmount = Number(invoiceAmountInput);
    const amountForPayload =
      invoiceAmountInput.trim() === "" ||
      !Number.isFinite(parsedAmount) ||
      parsedAmount < 0
        ? undefined
        : Math.max(0, Math.round(parsedAmount));
    const isoDate = toISODateString(invoiceDate);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const n = preview.applications.length;
    const baseLineAmount =
      amountForPayload !== undefined && n > 0
        ? Math.floor(amountForPayload / n)
        : undefined;
    const remainder =
      amountForPayload !== undefined && n > 0
        ? amountForPayload - baseLineAmount! * n
        : 0;
    try {
      const fresh = await saveOppositionTmMInvoice(leadId, {
        invoiceId: preview.invoiceId ?? undefined,
        lines: preview.applications.map((a, idx) => ({
          conflictingMarkId: a.conflictingMarkId,
          ...(discountForPayload !== undefined
            ? { discountPerApp: discountForPayload }
            : {}),
          ...(isoDate ? { invoiceDate: isoDate } : {}),
          ...(baseLineAmount !== undefined
            ? {
                lineAmount:
                  idx === 0 ? baseLineAmount + remainder : baseLineAmount,
              }
            : {}),
        })),
        includeInvoiceNo,
      });
      lastSavedRef.current = {
        invoiceDate: isoDate,
        discount: discountForPayload,
        invoiceAmount: amountForPayload,
        includeInvoiceNo,
      };
      setPreview(fresh);
      setPdf(null);
      return fresh;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save invoice"
      );
      return null;
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId || generating) return;
    setGenerating(true);
    try {
      const saved = await persistInvoice();
      if (!saved || !saved.invoiceId) return;
      const result = await generateOppositionTmMInvoicePdf(
        leadId,
        saved.invoiceId
      );
      setPdf(result);
      toast.success("Invoice PDF generated.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
    }
  };

  const ensurePdf = async (): Promise<typeof pdf> => {
    if (pdf?.downloadUrl) return pdf;
    if (!leadId) return null;
    const saved = await persistInvoice();
    if (!saved || !saved.invoiceId) return null;
    try {
      const result = await generateOppositionTmMInvoicePdf(
        leadId,
        saved.invoiceId
      );
      setPdf(result);
      return result;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
      return null;
    }
  };

  const handleDownload = async () => {
    if (!leadId || downloading) return;
    setDownloading(true);
    try {
      const generated = await ensurePdf();
      if (!generated) return;
      const result = await downloadFileFromUrlWithFallback(
        generated.downloadUrl,
        generated.filename ?? "invoice.pdf"
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const refreshSendPreview = async (invoiceId: string) => {
    if (!leadId) return;
    setPreviewLoading(true);
    try {
      const res = await previewSendOppositionTmMInvoice(leadId, invoiceId, {
        sendWhatsApp: true,
        sendEmail: true,
      });
      setPaymentMessage(res.paymentMessagePreview ?? "");
      setEmailPreview(
        res.emailPreview
          ? { ...res.emailPreview, filename: null, downloadUrl: null }
          : null
      );
    } catch {
      // non-critical
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreview = () => {
    if (!leadId) return;
    if (!validate()) return;
    setPreviewTab("details");
    setPreviewNumPages(null);
    setPreviewModalOpen(true);
    if (!pdf?.downloadUrl) {
      setPreviewPdfLoading(true);
    }
    void (async () => {
      try {
        const generated = await ensurePdf();
        if (generated && preview?.invoiceId) {
          await refreshSendPreview(preview.invoiceId);
        }
      } finally {
        setPreviewPdfLoading(false);
      }
    })();
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewNumPages(null);
    setPreviewTab("details");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (!validate()) return;

    if (hasSent) {
      setSaving(true);
      try {
        const saved = await persistInvoice();
        if (saved) {
          toast.success("Invoice saved successfully.");
          onSuccess?.(leadId);
        }
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    try {
      const saved = await persistInvoice();
      if (!saved || !saved.invoiceId) return;
      await refreshSendPreview(saved.invoiceId);
      setConfirmMode("send");
      setShowConfirmModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmProceed = async () => {
    if (!leadId || !preview?.invoiceId) return;
    setShowConfirmModal(false);
    setSaving(true);
    try {
      const invoiceId = preview.invoiceId;
      const [invoiceResult, paymentResult] = await Promise.allSettled([
        sendOppositionTmMInvoiceToClient(leadId, invoiceId, {
          sendWhatsApp: true,
          sendEmail: true,
        }),
        sendOppositionTmMInvoicePaymentMessage(leadId, invoiceId),
      ]);
      const invoiceOk = invoiceResult.status === "fulfilled";
      const paymentOk = paymentResult.status === "fulfilled";
      const invoiceErr =
        invoiceResult.status === "rejected"
          ? invoiceResult.reason instanceof Error
            ? invoiceResult.reason.message
            : "Failed to send invoice to client"
          : null;
      const paymentErr =
        paymentResult.status === "rejected"
          ? paymentResult.reason instanceof Error
            ? paymentResult.reason.message
            : "Failed to send payment message"
          : null;
      const isResend = confirmMode === "resend";

      if (invoiceOk && paymentOk) {
        setHasSent(true);
        toast.success(
          isResend
            ? "Invoice resent to client successfully."
            : "Invoice sent to client successfully."
        );
        onSuccess?.(leadId);
      } else if (invoiceOk && !paymentOk) {
        setHasSent(true);
        toast.warning(
          `Invoice sent to client, but payment message failed: ${paymentErr}`
        );
        onSuccess?.(leadId);
      } else if (!invoiceOk && paymentOk) {
        toast.warning(
          `Payment message sent, but sending invoice failed: ${invoiceErr}`
        );
      } else {
        toast.error(invoiceErr ?? "Failed to send invoice to client");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async () => {
    if (!leadId || saving) return;
    if (!validate()) return;
    setSaving(true);
    try {
      const saved = await persistInvoice();
      if (!saved || !saved.invoiceId) return;
      await refreshSendPreview(saved.invoiceId);
      setConfirmMode("resend");
      setShowConfirmModal(true);
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={FORM_STYLES}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={FORM_STYLES}
      >
        <p className="text-sm text-[#6D6D6D]">Loading invoice details…</p>
      </div>
    );
  }

  if (!preview || preview.applications.length === 0) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={FORM_STYLES}
      >
        <p className="text-sm text-[#6D6D6D]">
          No applications are eligible to be invoiced. Mark clientConfirmation
          = POSTPONE for at least one application in the previous step
          (Confirmation from Client for Opposition Hearing) first.
        </p>
      </div>
    );
  }

  const parsedDisplayAmount = Number(invoiceAmountInput);
  const displayInvoiceAmount =
    invoiceAmountInput.trim() !== "" && Number.isFinite(parsedDisplayAmount)
      ? Math.max(0, Math.round(parsedDisplayAmount))
      : preview.invoiceAmount;

  return (
    <form
      id={OPPOSITION_INVOICE_FOR_TMM_BEFORE_HEARING_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={FORM_STYLES}
    >
      {/* Row: Discount | Date of Invoice */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>
            Discount in Prof Fee for TM-M (Rs. per application)
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={discountInput}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^\d.]/g, "");
              const parts = raw.split(".");
              const v =
                parts.length > 1
                  ? `${parts[0]}.${parts.slice(1).join("")}`
                  : raw;
              setDiscountInput(v);
              const parsedDiscount = Number(v);
              const liveDiscount =
                v.trim() === "" || !Number.isFinite(parsedDiscount)
                  ? 0
                  : Math.max(0, parsedDiscount);
              const profFee = Number(preview.applications[0]?.profFeePerApp ?? 0);
              const n = preview.applications.length;
              const recomputed = Math.max(
                0,
                Math.round((profFee - liveDiscount) * n)
              );
              setInvoiceAmountInput(recomputed > 0 ? String(recomputed) : "");
              setFieldErrors((p) => ({
                ...p,
                discount: false,
                invoiceAmount: false,
              }));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            placeholder="0"
            className={`${INPUT_CLASS}${
              fieldErrors.discount ? " !border-red-500" : ""
            }`}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Date of Invoice for TM-M</label>
          <DateTimePicker
            value={invoiceDate}
            onChange={(d) => {
              setInvoiceDate(d);
              if (d) setFieldErrors((p) => ({ ...p, invoiceDate: false }));
            }}
            showTime={false}
            placeholder="Select Date"
            buttonClassName={`!h-9 !min-h-0 !text-[12px] rounded-[6px]${
              fieldErrors.invoiceDate ? " !border-red-500" : ""
            }`}
          />
        </div>
      </div>

      {/* Row: Invoice Amount (editable) | Invoice No. (read-only) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <label className={LABEL_CLASS}>
              Invoice Amount for TM-M before Hearing (Rs.)
            </label>
            <InvoiceAmountTooltip
              preview={preview}
              discountInput={discountInput}
            />
          </div>
          <input
            type="text"
            inputMode="decimal"
            value={invoiceAmountInput}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^\d.]/g, "");
              const parts = raw.split(".");
              const v =
                parts.length > 1
                  ? `${parts[0]}.${parts.slice(1).join("")}`
                  : raw;
              setInvoiceAmountInput(v);
              setFieldErrors((p) => ({ ...p, invoiceAmount: false }));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            placeholder="0"
            className={`${INPUT_CLASS}${
              fieldErrors.invoiceAmount ? " !border-red-500" : ""
            }`}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Invoice No. for TM-M</label>
          <input
            type="text"
            value={preview.invoiceNo ?? ""}
            placeholder="Auto-generated on save"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      {/* Include Invoice No checkbox */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="opposition-evidence-include-invoice-no"
          checked={includeInvoiceNo}
          onCheckedChange={(checked) => setIncludeInvoiceNo(Boolean(checked))}
        />
        <label
          htmlFor="opposition-evidence-include-invoice-no"
          className="text-xs md:text-sm text-[#222222] cursor-pointer"
          style={FORM_STYLES}
        >
          Include Invoice No. in PDF
        </label>
      </div>

      {/* Invoice Amount bar */}
      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 rounded-[8px] px-4 sm:px-5 py-4 sm:py-0 min-h-[72px] sm:h-[72px]"
        style={{ background: "#EBF0FF" }}
      >
        <div className="flex flex-row items-center gap-2 min-w-0">
          <img
            src={invoiceAmountCheck}
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span
            className="text-[12px] md:text-sm font-normal leading-5 text-[#222222]/90 truncate"
            style={FORM_STYLES}
          >
            Invoice Amount
          </span>
        </div>
        <span
          className="text-lg md:text-[22px] font-bold leading-8 md:leading-10 text-[#222222] shrink-0"
          style={FORM_STYLES}
        >
          {formatRupees(displayInvoiceAmount)}
        </span>
      </div>

      {/* Generate / Download / Preview */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img
            src={invoiceGenerate}
            alt=""
            className="w-[22px] h-[22px] flex-shrink-0"
          />
          <button
            type="button"
            onClick={handleGeneratePdf}
            disabled={generating}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
            style={FORM_STYLES}
          >
            {generating ? "Generating..." : pdf ? "Regenerate PDF" : "Generate PDF"}
          </button>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={FORM_STYLES}
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={handlePreview}
          disabled={generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={FORM_STYLES}
        >
          Preview
        </button>
      </div>

      {/* Phone + Resend */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSent && (
          <button
            type="button"
            disabled={saving}
            onClick={handleResend}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Resend Message"}
          </button>
        )}
      </div>

      {/* Confirm Modal */}
      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={
          confirmMode === "resend"
            ? "Resend invoice to client"
            : "Confirm invoice to client"
        }
        previewSlot={
          <div className="flex flex-col gap-4 p-4">
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                WhatsApp message (after invoice PDF)
              </p>
              <WhatsAppMessageBlock
                message={paymentMessage}
                loading={previewLoading}
              />
            </section>
            <div className="border-t border-dashed border-[#EEEEEE]" />
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Email (with invoice PDF)
              </p>
              <EmailPreviewBlock
                preview={emailPreview}
                loading={previewLoading}
                attachmentFallback={
                  pdf ? { filename: pdf.filename, url: pdf.downloadUrl } : null
                }
              />
            </section>
          </div>
        }
        onConfirm={() => {
          void handleConfirmProceed();
        }}
        disableConfirm={saving}
      />

      {/* Preview Modal */}
      {previewModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          onClick={closePreviewModal}
        >
          <div
            className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
              <span
                className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm"
                style={FORM_STYLES}
              >
                {pdf?.filename ?? "Invoice preview"}
              </span>
              <div className="flex items-center gap-3 shrink-0">
                {pdf?.downloadUrl && (
                  <a
                    href={pdf.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#2E3037] underline underline-offset-2"
                  >
                    Open in new tab
                  </a>
                )}
                <button
                  type="button"
                  onClick={closePreviewModal}
                  className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
                  aria-label="Close preview"
                >
                  <HiOutlineX className="size-5" strokeWidth={2} />
                </button>
              </div>
            </div>

            <div
              className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
              role="tablist"
            >
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "details"}
                onClick={() => setPreviewTab("details")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "details"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                Details
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "pdf"}
                onClick={() => setPreviewTab("pdf")}
                disabled={!pdf?.downloadUrl && !previewPdfLoading}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
                  previewTab === "pdf"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                {previewPdfLoading && !pdf?.downloadUrl
                  ? "Generated PDF (loading…)"
                  : "Generated PDF"}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "message"}
                onClick={() => setPreviewTab("message")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "message"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                WhatsApp Message
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "email"}
                onClick={() => setPreviewTab("email")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "email"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                Email
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4 flex justify-center">
              {previewTab === "details" ? (
                <div
                  className="flex w-full max-w-3xl flex-col gap-3"
                  style={FORM_STYLES}
                >
                  <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Invoice details
                    </p>
                    <InvoiceDetailsPreview
                      preview={preview}
                      invoiceDate={invoiceDate}
                    />
                  </section>
                  {preview.applications.length > 0 && (
                    <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                        Applications
                      </p>
                      <InvoiceApplicationsTable applications={preview.applications} />
                    </section>
                  )}
                </div>
              ) : previewTab === "pdf" ? (
                pdf?.downloadUrl ? (
                  <Document
                    file={pdf.downloadUrl}
                    onLoadSuccess={({ numPages: n }) => setPreviewNumPages(n)}
                    loading={
                      <p className="text-sm text-[#6d6d6d]">Loading PDF…</p>
                    }
                    error={
                      <p className="text-sm text-red-600">Failed to load PDF.</p>
                    }
                    className="flex flex-col items-center gap-4"
                  >
                    {previewNumPages != null &&
                      Array.from({ length: previewNumPages }, (_, i) => (
                        <Page
                          key={i + 1}
                          pageNumber={i + 1}
                          width={Math.min(
                            800,
                            typeof window !== "undefined"
                              ? window.innerWidth - 48
                              : 800
                          )}
                          renderTextLayer
                          renderAnnotationLayer
                        />
                      ))}
                  </Document>
                ) : previewPdfLoading ? (
                  <p className="text-sm text-[#6d6d6d]">Generating PDF…</p>
                ) : (
                  <p className="text-sm text-[#6D6D6D]">No PDF available.</p>
                )
              ) : previewTab === "message" ? (
                <div
                  className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                  style={FORM_STYLES}
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Will be sent to client WhatsApp after the invoice PDF
                  </p>
                  <WhatsAppMessageBlock
                    message={paymentMessage}
                    loading={previewLoading}
                  />
                </div>
              ) : (
                <div
                  className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                  style={FORM_STYLES}
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Will be emailed to the client with the invoice PDF
                  </p>
                  <EmailPreviewBlock
                    preview={emailPreview}
                    loading={previewLoading}
                    attachmentFallback={
                      pdf ? { filename: pdf.filename, url: pdf.downloadUrl } : null
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
